import React, { useCallback, useEffect } from 'react';

import { useAtom } from 'jotai';

import { FOCUSABLE_SELECTORS, ModalConfigT } from '@consumer-frontend/nc-utils';
import { Modal } from '@consumer-frontend/shared-ui';

import { modalConfigAtom } from '../../store';
import { NcButton } from '../NcButton';

import '@consumer-bootstrap/brands/netcredit/modal.scss';
import '@consumer-bootstrap/netcredit.scss';

export const NcModal: React.FC = () => {
  const [modalconfig, setModalConfig] = useAtom<ModalConfigT>(modalConfigAtom);
  const {
    body,
    modalOnSecondaryClick,
    modalOnClick,
    primaryText,
    secondaryText,
    show,
    title,
  } = modalconfig;

  const closeModal = useCallback(
    () => setModalConfig((prevConfig) => ({ ...prevConfig, show: false })),
    [setModalConfig],
  );

  const handlePrimaryClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      modalOnClick?.(event);
      closeModal();
    },
    [modalOnClick, closeModal],
  );

  const handleSecondaryClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      modalOnSecondaryClick?.(event);
      closeModal();
    },
    [modalOnSecondaryClick, closeModal],
  );

  // Focus the first focusable element when modal opens because a11y
  useEffect(
    () => {
      if (!show) return;

      // Wait for next tick to ensure modal is in DOM
      queueMicrotask(() => {
        const modal = document.querySelector('.nc-modal');
        const firstFocusable = modal?.querySelector(FOCUSABLE_SELECTORS.join(', '));

        if (firstFocusable) {
          (firstFocusable as HTMLElement).focus();
        }
      });
    },
    [show],
  );

  return (
    <Modal
      aria-labelledby={title}
      className="nc-modal"
      scrollable
      show={show}
      onHide={closeModal}
      title={title}
      body={body}
      footer={
        <div className={secondaryText ? 'default' : 'informational'}>
          <NcButton variant="primary" onClick={handlePrimaryClick}>
            {primaryText}
          </NcButton>
          {secondaryText && (
            <NcButton variant="outline-primary" onClick={handleSecondaryClick}>
              {secondaryText}
            </NcButton>
          )}
        </div>
      }
    />
  );
};
